import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState, useEffect } from 'react';

const ApplyPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    program: '',
    participants: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-apply';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Branch(브란치) 프로그램 신청 | 국제교류·진로교육 신청",
        "description": "Branch(브란치) 프로그램 신청 페이지입니다. 국제교류, 원데이 클래스, 대학 탐방, 진로 특강 프로그램을 신청하세요.",
        "url": siteUrl + "/apply",
        "inLanguage": "ko-KR",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Branch(브란치)",
          "url": siteUrl
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl + "/" },
            { "@type": "ListItem", "position": 2, "name": "프로그램 신청", "item": siteUrl + "/apply" }
          ]
        },
        "mainEntity": {
          "@type": "ContactPage",
          "name": "프로그램 신청 폼",
          "description": "Branch 프로그램 신청을 위한 문의 양식"
        }
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.email || !formData.program) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('올바른 이메일 주소를 입력해주세요.');
      return;
    }

    // Message length validation
    if (formData.message.length > 500) {
      alert('요청 사항은 500자 이내로 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('program', formData.program);
      formBody.append('participants', formData.participants);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6dup8eqd308q1a0a9ug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        // 운영자 이메일 알림 발송
        try {
          await fetch('https://cgrllgnxkomepkuvatcj.supabase.co/functions/v1/send-apply-notification', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: formData.name,
              phone: formData.phone,
              email: formData.email,
              program: formData.program,
              participants: formData.participants,
              message: formData.message,
            }),
          });
        } catch (emailError) {
          // 이메일 발송 실패해도 신청 자체는 성공으로 처리
          console.error('Email notification error:', emailError);
        }

        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          program: '',
          participants: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 px-4 md:px-6 bg-[#F5EFE7]">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-[#2C2416] mb-6 md:mb-8" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            프로그램 신청
          </h1>
          <p className="text-sm md:text-base text-[#6B5D4F] leading-relaxed" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            Branch와 함께 글로벌 역량을 키워보세요<br />
            신청서를 작성해주시면 빠르게 연락드리겠습니다
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <form id="program-apply-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8 md:space-y-10">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                이름 <span className="text-[#8B7355]">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
                placeholder="이름을 입력해주세요"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                연락처 <span className="text-[#8B7355]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
                placeholder="0507-1361-2932"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                이메일 <span className="text-[#8B7355]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
                placeholder="example@email.com"
              />
            </div>

            {/* Program */}
            <div>
              <label htmlFor="program" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                신청 프로그램명 <span className="text-[#8B7355]">*</span>
              </label>
              <select
                id="program"
                name="program"
                value={formData.program}
                onChange={handleChange}
                required
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm cursor-pointer transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
              >
                <option value="">프로그램을 선택해주세요</option>
                <option value="원데이클래스 (현장방문형)">원데이클래스 (현장방문형)</option>
                <option value="진로콘서트 (학교&기업으로 찾아가는 직업체험)">진로콘서트 (학교&기업으로 찾아가는 직업체험)</option>
                <option value="국제교류 프로그램">국제교류 프로그램</option>
                <option value="진로 특강">진로 특강</option>
                <option value="대학 탐방">대학 탐방</option>
                <option value="창업 & 자격증 취득과정">창업 & 자격증 취득과정</option>
                <option value="기타">기타 (요청 사항에 작성)</option>
              </select>
            </div>

            {/* Participants */}
            <div>
              <label htmlFor="participants" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                참여 인원
              </label>
              <input
                type="text"
                id="participants"
                name="participants"
                value={formData.participants}
                onChange={handleChange}
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
                placeholder="예: 20명, 1개 학급, 개인 등"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-[#2C2416] mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                요청 사항
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                maxLength={500}
                rows={8}
                className="w-full px-4 md:px-5 py-3 md:py-4 border border-[#D4C4B0] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm resize-none transition-colors"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
                placeholder="프로그램 관련 문의사항이나 요청사항을 자유롭게 작성해주세요 (최대 500자)"
              />
              <div className="text-right text-xs text-[#9B8B7A] mt-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                {formData.message.length} / 500자
              </div>
            </div>

            {/* Submit Status */}
            {submitStatus === 'success' && (
              <div className="p-4 md:p-5 bg-[#F5EFE7] border border-[#D4C4B0] rounded-sm">
                <div className="flex items-start gap-3 text-[#2C2416]">
                  <i className="ri-checkbox-circle-line text-xl text-[#8B7355] mt-0.5"></i>
                  <div>
                    <p className="font-medium text-sm mb-1" style={{ fontFamily: 'Noto Serif KR, serif' }}>신청이 완료되었습니다</p>
                    <p className="text-sm text-[#6B5D4F]" style={{ fontFamily: 'Noto Serif KR, serif' }}>빠른 시일 내에 연락드리겠습니다</p>
                  </div>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 md:p-5 bg-red-50 border border-red-200 rounded-sm">
                <div className="flex items-start gap-3 text-red-800">
                  <i className="ri-error-warning-line text-xl mt-0.5"></i>
                  <div>
                    <p className="font-medium text-sm mb-1" style={{ fontFamily: 'Noto Serif KR, serif' }}>신청 중 오류가 발생했습니다</p>
                    <p className="text-sm" style={{ fontFamily: 'Noto Serif KR, serif' }}>잠시 후 다시 시도해주세요</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#8B7355] hover:bg-[#6F5C44] text-white py-4 md:py-5 rounded-sm font-normal text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Noto Serif KR, serif' }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  신청 중...
                </span>
              ) : (
                '신청하기'
              )}
            </button>

            <p className="text-center text-xs md:text-sm text-[#9B8B7A] mt-6" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              신청서 제출 후 1-2일 내에 담당자가 연락드립니다
            </p>
          </form>

          {/* Contact Info */}
          <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center py-8 md:py-10 border border-[#D4C4B0] rounded-sm">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 md:mb-5">
                <i className="ri-phone-line text-2xl text-[#8B7355]"></i>
              </div>
              <h3 className="text-sm font-medium text-[#2C2416] mb-2 md:mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>전화 문의</h3>
              <p className="text-sm text-[#6B5D4F]" style={{ fontFamily: 'Noto Serif KR, serif' }}>0507-1361-2932</p>
            </div>

            <div className="text-center py-8 md:py-10 border border-[#D4C4B0] rounded-sm">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 md:mb-5">
                <i className="ri-map-pin-line text-2xl text-[#8B7355]"></i>
              </div>
              <h3 className="text-sm font-medium text-[#2C2416] mb-2 md:mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>오시는 길</h3>
              <p className="text-sm text-[#6B5D4F]" style={{ fontFamily: 'Noto Serif KR, serif' }}>서울시 강동구<br />올림픽로 98가길 47</p>
            </div>

            <div className="text-center py-8 md:py-10 border border-[#D4C4B0] rounded-sm">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 md:mb-5">
                <i className="ri-time-line text-2xl text-[#8B7355]"></i>
              </div>
              <h3 className="text-sm font-medium text-[#2C2416] mb-2 md:mb-3" style={{ fontFamily: 'Noto Serif KR, serif' }}>운영 시간</h3>
              <p className="text-sm text-[#6B5D4F]" style={{ fontFamily: 'Noto Serif KR, serif' }}>평일 09:00 - 18:00<br />(주말 및 공휴일 휴무)</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplyPage;
